import type { LanguageCode } from '@sparted/shared-library/business/types';
import { t } from 'i18next';
import { useCallback } from 'react';

import { COLORS } from 'Components/foundation';
import { redirect } from 'Libs/routing/redirect';
import { enqueueBasicAlert } from 'Pages/AlertManager';
import useArchiveAction from 'Pages/Activity/hooks/useArchiveAction';
import { ALERT_DURATION } from 'Pages/Content/GameplaysForm/gameplay.constants';
import { getCurrentBOLanguageOrDefault } from 'Services/language/languageService';
import { duplicateBreakingNews, duplicateCampaign } from 'Store/entities/activity/activity.api';

import { ROUTE_MAP } from '../Item.constants';

export const useActivityActions = (activityId: number, status: string, type?: string) => {
  const { handleArchiveClick, archiveWarningModal } = useArchiveAction();

  // handle command or control click on item by opening the item in a new tab
  const handleClickItem = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!type) return;

      if (type === 'campaign') {
        if (event.metaKey || event.ctrlKey) {
          window.open(`${ROUTE_MAP.campaign}/${activityId}`, '_blank');
        } else {
          redirect(`${ROUTE_MAP.campaign}/${activityId}`);
        }
      } else if (type === 'breaking-news') {
        if (event.metaKey || event.ctrlKey) {
          window.open(`${ROUTE_MAP['breaking-news']}/${activityId}`, '_blank');
        } else {
          redirect(`${ROUTE_MAP['breaking-news']}/${activityId}`);
        }
      }
    },
    [activityId, type],
  );

  const handleSelectLanguage = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>, language: LanguageCode) => {
      event.stopPropagation();

      if (type === 'campaign') {
        window.open(`${ROUTE_MAP.campaign}/${activityId}?language=${language}`, '_blank');
      } else if (type === 'breaking-news') {
        window.open(`${ROUTE_MAP['breaking-news']}/${activityId}?language=${language}`, '_blank');
      }
    },
    [activityId, type],
  );

  const duplicateActivity = useCallback(async () => {
    if (!type) return;

    const { languageCode } = getCurrentBOLanguageOrDefault();
    try {
      if (type === 'campaign') {
        const duplicatedCampaign = await duplicateCampaign(activityId, languageCode);
        redirect(`${ROUTE_MAP.campaign}/${duplicatedCampaign.id}`);
      } else if (type === 'breaking-news') {
        const duplicatedBreakingNews = await duplicateBreakingNews(activityId, languageCode);
        redirect(`${ROUTE_MAP['breaking-news']}/${duplicatedBreakingNews.id}`);
      }
      enqueueBasicAlert({
        id: 'activity_duplication_failed',
        status: 'success',
        title: t('activities:alerts.success.duplicate'),
      });
    } catch (error) {
      enqueueBasicAlert(
        {
          id: 'activity_duplication_failed',
          status: 'error',
          title: t('activities:alerts.errors.duplicate_error_message'),
        },
        ALERT_DURATION,
      );
    }
  }, [activityId, type]);

  // Create options array for dropdown
  const options = [
    {
      color: COLORS.TEXT.DEFAULT,
      title: t('activities:actions.duplicate'),
      callback: () => duplicateActivity(),
    },
    {
      color: COLORS.TEXT.DEFAULT,
      title: status === 'archived' ? t('activities:actions.unarchive') : t('activities:actions.archive'),
      callback: () => handleArchiveClick(activityId, status === 'archived'),
    },
  ];

  return {
    handleClickItem,
    handleSelectLanguage,
    duplicateActivity,
    options,
    archiveWarningModal,
  };
};
